/**
 * TanStack Router with code-style route definitions (not file-based — we
 * keep the router declarations close to the pages they describe).
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { AppLayout } from "./layouts/AppLayout.js";
import { LoginPage } from "./pages/LoginPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { LoansPage } from "./pages/LoansPage.js";
import { LoanDetailPage } from "./pages/LoanDetailPage.js";
import { CustomersPage } from "./pages/CustomersPage.js";
import { CustomerDetailPage } from "./pages/CustomerDetailPage.js";
import { DealersPage } from "./pages/DealersPage.js";
import { DealerDetailPage } from "./pages/DealerDetailPage.js";
import { DevicesPage } from "./pages/DevicesPage.js";
import { DeviceDetailPage } from "./pages/DeviceDetailPage.js";
import { DeviceSmsPage } from "./pages/DeviceSmsPage.js";
import { FleetHealthPage } from "./pages/FleetHealthPage.js";
import { ChangePasswordPage } from "./pages/ChangePasswordPage.js";
import { ReportsPage } from "./pages/ReportsPage.js";
import { ModelPricesPage } from "./pages/ModelPricesPage.js";
import { PaymentsPage } from "./pages/PaymentsPage.js";
import { ContractsPage } from "./pages/ContractsPage.js";
import { ContractDetailPage } from "./pages/ContractDetailPage.js";
import { ContractSigningPage } from "./pages/ContractSigningPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { PaymentSandboxPage } from "./pages/PaymentSandboxPage.js";
import { AuditPage } from "./pages/AuditPage.js";
import { ApprovalsPage } from "./pages/ApprovalsPage.js";
import { OutboxDlqPage } from "./pages/OutboxDlqPage.js";
import { UsersPage } from "./pages/UsersPage.js";
import { UserDetailPage } from "./pages/UserDetailPage.js";
import { RolesPage } from "./pages/RolesPage.js";
import { CashPage } from "./pages/CashPage.js";
import { ExpensesPage } from "./pages/ExpensesPage.js";
import { StockPage } from "./pages/StockPage.js";
import { browserTokens } from "./lib/tokens.js";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  beforeLoad: () => {
    if (browserTokens.getAccessToken()) {
      throw redirect({ to: "/" });
    }
  },
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: AppLayout,
  beforeLoad: () => {
    if (!browserTokens.getAccessToken()) {
      throw redirect({ to: "/login" });
    }
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: DashboardPage,
});

// Loans
const loansRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/loans",
  component: LoansPage,
  // Drill-down search params: dashboard status-count cards + the
  // CustomersPage "loans" pivot deep-link here with a pre-applied
  // filter. Kept loose (plain strings) so the router stays free of the
  // domain LoanStatus union; LoansPage seeds its filter state from them.
  validateSearch: (search: Record<string, unknown>): { status?: string; customerId?: string } => {
    // Build conditionally — under exactOptionalPropertyTypes an explicit
    // `undefined` value isn't assignable to an optional key, and it also
    // keeps the URL clean (no ?status=undefined).
    const out: { status?: string; customerId?: string } = {};
    if (typeof search.status === "string") out.status = search.status;
    if (typeof search.customerId === "string") out.customerId = search.customerId;
    return out;
  },
});
const loanSignContractRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/loans/$id/sign-contract",
  component: ContractSigningPage,
});
const loanDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/loans/$id",
  component: LoanDetailPage,
});

// Customers
const customersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/customers",
  component: CustomersPage,
});
const customerDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/customers/$id",
  component: CustomerDetailPage,
});

// Dealers
const dealersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/dealers",
  component: DealersPage,
});
const dealerDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/dealers/$id",
  component: DealerDetailPage,
});

// Devices
const devicesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/devices",
  component: DevicesPage,
  // Drill-down from the Fleet page: filter the list to one DPC version, the
  // not-reported bucket, or one control provider. Kept loose (plain strings)
  // so the router stays free of domain unions; DevicesPage applies them.
  validateSearch: (
    search: Record<string, unknown>,
  ): { versionCode?: string; notReported?: string; provider?: string } => {
    const out: { versionCode?: string; notReported?: string; provider?: string } = {};
    if (typeof search.versionCode === "string") out.versionCode = search.versionCode;
    if (search.notReported === "1" || search.notReported === "true") out.notReported = "1";
    if (typeof search.provider === "string") out.provider = search.provider;
    return out;
  },
});
const deviceDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/devices/$id",
  component: DeviceDetailPage,
});
// Dedicated SMS conversation viewer (parental monitoring) — reached from the
// device page's monitoring card. Keyset-paginated + grouped by thread.
const deviceSmsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/devices/$id/sms",
  component: DeviceSmsPage,
});
// Fleet health dashboard — aggregate view above /devices listing.
const fleetHealthRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/fleet",
  component: FleetHealthPage,
});
// Self-service / force-change password. Routed under protectedRoute
// so an unauth user is bounced to /login; the back-end's
// PasswordChangeGuard ensures the page is the ONLY mutating endpoint
// accessible when the bearer carries pwdChange=true.
const changePasswordRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/auth/change-password",
  component: ChangePasswordPage,
});
// Financial reports — gated client-side on `report.read`; server
// rejects callers without it. Lives in the Overview group of the
// sidebar alongside Dashboard + Fleet health.
const reportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/reports",
  component: ReportsPage,
});
const modelPricesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/model-prices",
  component: ModelPricesPage,
});
// Accounting — cash & account balances from the general ledger (UPGRADE-01).
const cashRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/accounting/cash",
  component: CashPage,
});
const expensesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/accounting/expenses",
  component: ExpensesPage,
});
const stockRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/accounting/stock",
  component: StockPage,
});

// Payments
const paymentsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/payments",
  component: PaymentsPage,
});

// Contracts
const contractsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/contracts",
  component: ContractsPage,
});
const contractDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/contracts/$id",
  component: ContractDetailPage,
});

// Audit
const auditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/audit",
  component: AuditPage,
});

// Approvals (maker-checker)
const approvalsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/approvals",
  component: ApprovalsPage,
});

// Outbox dead-letter queue (admin ops)
const outboxDlqRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/outbox",
  component: OutboxDlqPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/settings",
  component: SettingsPage,
});
const paymentSandboxRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/payment-sandbox",
  component: PaymentSandboxPage,
});

// IAM — user management + role assignment
const usersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/iam/users",
  component: UsersPage,
});
const userDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/iam/users/$id",
  component: UserDetailPage,
});
const rolesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/iam/roles",
  component: RolesPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    loansRoute,
    loanDetailRoute,
    loanSignContractRoute,
    customersRoute,
    customerDetailRoute,
    dealersRoute,
    dealerDetailRoute,
    devicesRoute,
    fleetHealthRoute,
    changePasswordRoute,
    reportsRoute,
    deviceDetailRoute,
    deviceSmsRoute,
    modelPricesRoute,
    cashRoute,
    expensesRoute,
    stockRoute,
    paymentsRoute,
    contractsRoute,
    contractDetailRoute,
    auditRoute,
    approvalsRoute,
    outboxDlqRoute,
    settingsRoute,
    paymentSandboxRoute,
    usersRoute,
    userDetailRoute,
    rolesRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
